import * as React from 'react'
import { connect } from 'react-redux'
import { RouteComponentProps, withRouter } from 'react-router'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  Table,
  Button,
  ButtonGroup
} from 'reactstrap'

import { Pagination } from 'src/lib/interfaces/Pagination.interface'
import { Termination } from 'src/lib/interfaces/Termination.interface'

import * as terminationsActions from '../../store/terminations/actions'

import Breadcrumbs from '../Breadcrumbs.component'

import { formatName } from 'src/lib/helpers/format'
import { terminationStatusToString } from 'src/utils/termination'
import withConfirm, { WithConfirmProps } from 'src/lib/helpers/withConfirm'

const get = require('lodash/get')
const map = require('lodash/map')

export interface TerminationsListState {
  pageStatus: 'loading' | 'done'
}

export interface TerminationsListProps extends WithConfirmProps, RouteComponentProps {
  isFetching: boolean
  terminations?: Termination[]
  resetTerminationsList: () => void
  pagination?: Pagination<Termination>
  fetchTerminations: (page?: number) => void
  deleteTermination: (terminationId: string, next?: (err?: any) => void) => void
  confirmTermination: (terminationId: string, next?: (err?: any, termination?: Termination) => void) => void
}

class TerminationsListPage extends React.PureComponent<TerminationsListProps, TerminationsListState> {
  constructor(props: TerminationsListProps) {
    super(props)

    const { pagination } = props

    this.state = {
      pageStatus: !pagination ? 'loading' : 'done'
    }
  }

  componentDidMount() {
    this.props.fetchTerminations()
  }

  componentDidUpdate(prevProps: TerminationsListProps) {
    if (this.props.terminations && this.props.terminations !== prevProps.terminations) {
      this.setState(() => ({ pageStatus: 'done' }))
    }
  }

  componentWillUnmount() {
    this.props.resetTerminationsList()
  }

  handleSelectTermination(e: React.MouseEvent<HTMLTableRowElement>, termination: Termination) {
    e.preventDefault()
    this.props.history.push(`/terminations/${termination.id}`)
  }

  handleLoadMore() {
    const { pagination } = this.props
    this.props.fetchTerminations(pagination!.number + 1)
  }

  handleConfirmTermination(termination: Termination) {
    this.props.confirmTermination(termination.id, (err?: any) => {
      if (!err) {
        this.props.resetTerminationsList()
        this.props.fetchTerminations()
      }
    })
  }

  handleDeleteTermination(termination: Termination) {
    this.props.deleteTermination(termination.id, (err?: any) => {
      if (!err) {
        this.props.resetTerminationsList()
        this.props.fetchTerminations()
      }
    })
  }

  renderActionButtons(termination: Termination) {
    const defaultStyle = { width: '25px', padding: '2px 0' }

    return (
      <ButtonGroup size="sm">
        <Button
          size="sm"
          color="dark"
          className="px-0"
          style={defaultStyle}
          onClick={e => {
            e.stopPropagation()
            this.props.confirm(
              () => this.handleDeleteTermination(termination),
              {
                description:
                  <span>
                    Are you sure you want to delete <b>{formatName(termination.employee)}</b>'s termination?
                  </span>
              })
          }}
        >
          <FontAwesomeIcon icon={['fas', 'times']} />
        </Button>

        <Button
          size="sm"
          color="primary"
          className="px-0"
          style={defaultStyle}
          onClick={e => {
            e.stopPropagation()
            this.props.confirm(
              () => this.handleConfirmTermination(termination),
              {
                description:
                  <span>
                    Are you sure you want to confirm <b>{formatName(termination.employee)}</b>'s termination?
                  </span>
              })
          }}
        >
          <FontAwesomeIcon icon={['fas', 'check']} />
        </Button>
      </ButtonGroup>
    )
  }

  render() {
    const { pageStatus } = this.state
    const { isFetching, pagination, terminations } = this.props

    if (pageStatus === 'loading' || !terminations) {
      return (
        <div className="p-3">Loading...</div>
      )
    }

    return (
      <div>
        <Breadcrumbs breadcrumbs={[
          { name: 'Dashboard', link: '/dashboard' },
          { name: 'Terminations', active: true },
        ]} />

        <div className="page">
          <h5 className="title">Terminations</h5>

          <Table hover className="table table-striped">
            <thead>
              <tr>
                <th className="w-30">Name</th>
                <th className="w-25">Location</th>
                <th className="w-25">Reason</th>
                <th className="w-auto">Voluntary</th>
                <th className="w-auto">Actions</th>
              </tr>
            </thead>
            <tbody>
              {map(terminations, termination => (
                <tr key={termination.id} className="cursor-pointer" onClick={e => this.handleSelectTermination(e, termination)}>
                  <td>
                    {formatName(termination.employee)}
                  </td>
                  <td>{get(termination, 'employee.location.name')}</td>
                  <td>{terminationStatusToString(termination.reason)}</td>
                  <td>{termination.voluntary ? 'Yes' : 'No'}</td>
                  <td className="py-2">{this.renderActionButtons(termination)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
          {!get(pagination, 'last', true) &&
            <div className="w-100 d-flex aign-items-center justify-content-center">
              <Button color="primary" className="ggbutton" disabled={isFetching} onClick={() => this.handleLoadMore()}>
                {isFetching && <FontAwesomeIcon spin icon={['fas', 'spinner']} className="mr-2" />}
                Load More
              </Button>
            </div>
          }
        </div>
      </div>
    )
  }
}

const mapStateToProps = ({ terminations }) => ({
  isFetching: terminations.isFetching,
  pagination: terminations.pagination,
  terminations: terminations.terminations,
})

const mapDispatchToProps = (dispatch) => ({
  resetTerminationsList: () => dispatch(terminationsActions.resetTerminationsList()),
  fetchTerminations: (page?: number) => dispatch(terminationsActions.fetchTerminations(page)),
  deleteTermination: (terminationId: string, next?: (err?: any) => void) =>
    dispatch(terminationsActions.deleteTermination(terminationId, next)),
  confirmTermination: (terminationId: string, next?: (err?: any, termination?: Termination) => void) =>
    dispatch(terminationsActions.confirmTermination(terminationId, next))
})

export default withConfirm(withRouter(connect(mapStateToProps, mapDispatchToProps)(TerminationsListPage)))



// WEBPACK FOOTER //
// ./src/routes/terminations/TerminationsList.page.tsx